import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../i18n';
import { X, PiggyBank, Building2, Smartphone } from 'lucide-react';

const CURRENCIES = [
  { code: 'FCFA', label: 'FCFA (Franc CFA)' },
  { code: 'EUR',  label: 'EUR (Euro)' },
  { code: 'USD',  label: 'USD (Dollar US)' },
  { code: 'GBP',  label: 'GBP (Livre sterling)' },
  { code: 'MAD',  label: 'MAD (Dirham marocain)' },
  { code: 'XOF',  label: 'XOF (Franc CFA UEMOA)' },
];

const MOBILE_TYPES = ['Wave', 'Orange Money', 'MTN Mobile Money', 'Airtel Money', 'Moov Money', 'Free Money', 'Autre'];

export default function ModalCagnotte({ open, onClose, onSave, initial }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    libelle: '', description: '', objectif_mensuel: '',
    devise: 'FCFA',
    compte_bancaire: '', banque_nom: '',
    mobile_money_numero: '', mobile_money_type: 'Wave', mobile_money_lien: '',
  });
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('general');
      setForm(initial ? {
        libelle: initial.libelle || '',
        description: initial.description || '',
        objectif_mensuel: initial.objectif_mensuel || '',
        devise: initial.devise || 'FCFA',
        compte_bancaire: initial.compte_bancaire || '',
        banque_nom: initial.banque_nom || '',
        mobile_money_numero: initial.mobile_money_numero || '',
        mobile_money_type: initial.mobile_money_type || 'Wave',
        mobile_money_lien: initial.mobile_money_lien || '',
      } : {
        libelle: '', description: '', objectif_mensuel: '', devise: 'FCFA',
        compte_bancaire: '', banque_nom: '',
        mobile_money_numero: '', mobile_money_type: 'Wave', mobile_money_lien: '',
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, objectif_mensuel: Number(form.objectif_mensuel) || 0 });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'general', label: 'Général', icon: PiggyBank },
    { key: 'bank',    label: 'Banque',  icon: Building2 },
    { key: 'mobile',  label: 'Mobile Money', icon: Smartphone },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 520, width: '95%' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PiggyBank size={20} style={{ color: 'var(--gold)' }} />
            <h2 className="modal-title">{initial ? t.cagnotte.edit : t.cagnotte.add}</h2>
          </div>
          <button className="modal-close" onClick={onClose} type="button"><X size={16} /></button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px', gap: 4 }}>
          {tabs.map(tab_ => {
            const Icon = tab_.icon;
            const active = tab === tab_.key;
            return (
              <button
                key={tab_.key}
                type="button"
                onClick={() => setTab(tab_.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '0.85rem',
                  color: active ? 'var(--primary)' : 'var(--text-muted)',
                  borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
                  fontWeight: active ? 600 : 400,
                  transition: 'all 0.2s',
                  marginBottom: -1,
                }}
              >
                <Icon size={14} />
                {tab_.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* General Tab */}
            {tab === 'general' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.libelle} <span className="required">*</span></label>
                  <input
                    id="cagnotte-libelle"
                    className="form-control"
                    value={form.libelle}
                    onChange={set('libelle')}
                    required
                    placeholder="Ex: Tontine Famille 2025"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.description}</label>
                  <textarea
                    id="cagnotte-description"
                    className="form-control"
                    value={form.description}
                    onChange={set('description')}
                    rows={3}
                    placeholder="Description du fonds..."
                    style={{ resize: 'vertical', minHeight: 72 }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">{t.cagnotte.objectifMensuel}</label>
                    <input
                      id="cagnotte-objectif"
                      type="number"
                      min="0"
                      className="form-control"
                      value={form.objectif_mensuel}
                      onChange={set('objectif_mensuel')}
                      placeholder="Ex: 25000"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t.cagnotte.devise}</label>
                    <select id="cagnotte-devise" className="form-select" value={form.devise} onChange={set('devise')}>
                      {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Bank Tab */}
            {tab === 'bank' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.banqueNom}</label>
                  <input
                    id="cagnotte-banque-nom"
                    className="form-control"
                    value={form.banque_nom}
                    onChange={set('banque_nom')}
                    placeholder="Ex: Coris Bank, Ecobank..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.compteBancaire}</label>
                  <input
                    id="cagnotte-compte"
                    className="form-control"
                    value={form.compte_bancaire}
                    onChange={set('compte_bancaire')}
                    placeholder="IBAN ou numéro de compte"
                  />
                </div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0, padding: '8px 0' }}>
                  💡 Ces informations seront visibles par tous les membres de la cagnotte.
                </p>
              </>
            )}

            {/* Mobile Money Tab */}
            {tab === 'mobile' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.mobileType}</label>
                  <select id="cagnotte-mobile-type" className="form-select" value={form.mobile_money_type} onChange={set('mobile_money_type')}>
                    {MOBILE_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.mobileNumero}</label>
                  <input
                    id="cagnotte-mobile-numero"
                    className="form-control"
                    value={form.mobile_money_numero}
                    onChange={set('mobile_money_numero')}
                    placeholder="Ex: +226 70 00 00 00"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.cagnotte.mobileLien}</label>
                  <input
                    id="cagnotte-mobile-lien"
                    type="url"
                    className="form-control"
                    value={form.mobile_money_lien}
                    onChange={set('mobile_money_lien')}
                    placeholder="https://pay.wave.com/..."
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !form.libelle.trim()}>
              {saving ? t.common.loading : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
